<script setup>
import ApikomContentTable from "@frontJs/components/tables/projectManagement/seo/ApikomContentTable.vue";
import ECommerceCategoryContentTable from "@frontJs/components/tables/projectManagement/seo/ECommerceCategoryContentTable.vue";
import ECommerceProductContentTable from "@frontJs/components/tables/projectManagement/seo/ECommerceProductContentTable.vue";
import TenantReportApikomContentTable from "@frontJs/components/tables/projectManagement/seo/tenantReports/ApikomContentTable.vue";
import ProjectManagementLayout from "@frontJs/layouts/ProjectManagementLayout.vue";
import { Head } from "@inertiajs/vue3";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import PageTitle from "@js/components/titles/PageTitle.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { useRoute } from "@js/utils/route";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { tenant, websiteVariants } = defineProps({
  tenant: {
    type: Object,
    required: false,
  },
  websiteVariants: {
    type: Array,
    required: true,
  },
});

const { route } = useRoute();

const loading = ref(true);

const data = ref({
  ongoing: 0,
  pending: 0,
  apikomStandard: 0,
  apikomPremium: 0,
  categoryStandard: 0,
  categoryPremium: 0,
  productStandard: 0,
  productPremium: 0,
});

const selectedTab = ref(0);

function updateData() {
  if (tenant) {
    loading.value = true;

    let promises = [
      fetch(
        route("front.projectManagement.seo.postApikomContentCount", {
          tenant: tenant.id,
        }),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ongoing: true }),
        },
      ),
      fetch(
        route("front.projectManagement.seo.postApikomContentCount", {
          tenant: tenant.id,
        }),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ pending: true }),
        },
      ),
      fetch(
        route("front.projectManagement.seo.postApikomContentCount", {
          tenant: tenant.id,
        }),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ done: true, standard: true }),
        },
      ),
      fetch(
        route("front.projectManagement.seo.postApikomContentCount", {
          tenant: tenant.id,
        }),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ done: true, premium: true }),
        },
      ),
      ...(tenant.type === "e-commerce"
        ? [
            fetch(
              route(
                "front.projectManagement.seo.postECommerceCategoryContentsCount",
                { tenant: tenant.id },
              ),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  standard: true,
                  hideNotDoneSeoApikomContent: true,
                }),
              },
            ),
            fetch(
              route(
                "front.projectManagement.seo.postECommerceCategoryContentsCount",
                { tenant: tenant.id },
              ),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  premium: true,
                  hideNotDoneSeoApikomContent: true,
                }),
              },
            ),
            fetch(
              route(
                "front.projectManagement.seo.postECommerceProductContentsCount",
                {
                  tenant: tenant.id,
                },
              ),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  standard: true,
                  hideNotDoneSeoApikomContent: true,
                }),
              },
            ),
            fetch(
              route(
                "front.projectManagement.seo.postECommerceProductContentsCount",
                {
                  tenant: tenant.id,
                },
              ),
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  premium: true,
                  hideNotDoneSeoApikomContent: true,
                }),
              },
            ),
          ]
        : []),
    ];

    Promise.all(promises)
      .then((responses) => {
        return Promise.all(responses.map((response) => response.json()));
      })
      .then((responseData) => {
        data.value.ongoing = responseData[0].count;
        data.value.pending = responseData[1].count;
        data.value.apikomStandard = responseData[2].count;
        data.value.apikomPremium = responseData[3].count;

        if (tenant.type === "e-commerce") {
          data.value.categoryStandard = responseData[4].count;
          data.value.categoryPremium = responseData[5].count;
          data.value.productStandard = responseData[6].count;
          data.value.productPremium = responseData[7].count;
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        loading.value = false;
      });
  }
}

function changeTab(index) {
  selectedTab.value = index;
}

onMounted(() => {
  if (tenant) {
    updateData();
  }
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
  <Head v-if="tenant">
    <title>
      {{ tenant.name }} - {{ t("title.apikomContents") }} -
      {{ t("title.seo") }} - {{ t("title.projectManagement") }} -
      {{ t("title.datakepler") }}
    </title>
  </Head>

  <Head v-else>
    <title>
      {{ t("title.apikomContents") }} - {{ t("title.seo") }} -
      {{ t("title.projectManagement") }} - {{ t("title.datakepler") }}
    </title>
  </Head>

  <PageTitle v-if="tenant">
    {{ t("title.projectManagement") }} - {{ t("title.seo") }} -
    {{ t("title.apikomContents") }} - {{ tenant.name }}
  </PageTitle>

  <PageTitle v-else>
    {{ t("title.projectManagement") }} - {{ t("title.seo") }} -
    {{ t("title.apikomContents") }}
  </PageTitle>

  <template v-if="tenant">
    <LoadingSpinner v-if="loading" />

    <TabGroup
      v-else
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        ongoing: t('title.toProcess') + ' (' + data.ongoing + ')',
        pending: t('title.pending') + ' (' + data.pending + ')',
        apikomStandard:
          t('title.otherStandard') + ' (' + data.apikomStandard + ')',
        apikomPremium:
          t('title.otherPremium') + ' (' + data.apikomPremium + ')',
        ...(tenant.type === 'e-commerce'
          ? {
              categoryStandard:
                t('title.categoryStandard') +
                ' (' +
                data.categoryStandard +
                ')',
              categoryPremium:
                t('title.categoryPremium') + ' (' + data.categoryPremium + ')',
              productStandard:
                t('title.productStandard') + ' (' + data.productStandard + ')',
              productPremium:
                t('title.productPremium') + ' (' + data.productPremium + ')',
            }
          : {}),
      }"
    >
      <template #panels>
        <TabPanel without-background unmount>
          <ApikomContentTable
            :tenant="tenant"
            :website-variants="websiteVariants"
            ongoing
          />
        </TabPanel>

        <TabPanel without-background unmount>
          <ApikomContentTable
            :tenant="tenant"
            :website-variants="websiteVariants"
            pending
          />
        </TabPanel>

        <TabPanel without-background unmount>
          <ApikomContentTable
            :tenant="tenant"
            :website-variants="websiteVariants"
            standard
            done
          />
        </TabPanel>

        <TabPanel without-background unmount>
          <ApikomContentTable
            :tenant="tenant"
            :website-variants="websiteVariants"
            premium
            done
          />
        </TabPanel>

        <TabPanel
          v-if="tenant.type === 'e-commerce'"
          without-background
          unmount
        >
          <ECommerceCategoryContentTable
            :tenant="tenant"
            standard
            hide-not-done-seo-apikom-content
          />
        </TabPanel>

        <TabPanel
          v-if="tenant.type === 'e-commerce'"
          without-background
          unmount
        >
          <ECommerceCategoryContentTable
            :tenant="tenant"
            premium
            hide-not-done-seo-apikom-content
          />
        </TabPanel>

        <TabPanel
          v-if="tenant.type === 'e-commerce'"
          without-background
          unmount
        >
          <ECommerceProductContentTable
            :tenant="tenant"
            standard
            hide-not-done-seo-apikom-content
          />
        </TabPanel>

        <TabPanel
          v-if="tenant.type === 'e-commerce'"
          without-background
          unmount
        >
          <ECommerceProductContentTable
            :tenant="tenant"
            premium
            hide-not-done-seo-apikom-content
          />
        </TabPanel>
      </template>
    </TabGroup>
  </template>

  <TabGroup
    v-else
    :selected-tab="selectedTab"
    :change-tab="changeTab"
    :tabs="{
      all: t('label.all'),
      standard: t('label.standard'),
      premium: t('label.premium'),
    }"
  >
    <template #panels>
      <TabPanel without-background unmount>
        <TenantReportApikomContentTable />
      </TabPanel>

      <TabPanel without-background unmount>
        <TenantReportApikomContentTable standard />
      </TabPanel>

      <TabPanel without-background unmount>
        <TenantReportApikomContentTable premium />
      </TabPanel>
    </template>
  </TabGroup>
</template>
